import {
    VisitRequestTableRowFragment,
    VisitTableRowFragment,
} from '@/components/bulkQueue/tables/queries.gql.types';
import { Store, createStore } from '@/utils/store';
import { useCallback, useRef, useState } from 'react';

export type BulkState = 'loading' | 'idle' | 'submitting';

export type Entity = VisitTableRowFragment | VisitRequestTableRowFragment;

export const useBulkState = (loading: boolean, submitting: boolean): BulkState => {
    if (submitting) return 'submitting';
    return loading ? 'loading' : 'idle';
};

export const useBulkSelectionStore = (): [
    store: Store<boolean>,
    setValue: (rowId: string, isSelected: boolean | null) => void,
    selectedCount: number,
    resetSelection: () => void,
] => {
    const selectionStoreRef = useRef(createStore<boolean>());
    const selectionStore = selectionStoreRef.current;
    const [selectedCount, setSelectedCount] = useState(0);

    const setValue = useCallback(
        (rowId: string, isSelected: boolean | null) => {
            if (isSelected === null) {
                selectionStore.setValue(rowId, !selectionStore.getSnapshot(rowId));
            } else {
                selectionStore.setValue(rowId, isSelected);
            }
            let count = 0;
            selectionStore.getAll().forEach((v) => {
                if (v) count++;
            });
            setSelectedCount(count);
        },
        [selectionStore],
    );

    const resetSelection = useCallback(() => {
        selectionStore.setAll(new Map());
        setSelectedCount(0);
    }, [selectionStore]);

    return [selectionStore, setValue, selectedCount, resetSelection];
};

export const getOrCreateEntityMap = <TValue>(
    map: Map<string, Map<string, TValue>>,
    entityId: string,
): Map<string, TValue> => {
    const existing = map.get(entityId);
    if (existing) {
        return existing;
    }
    const next = new Map<string, TValue>();
    map.set(entityId, next);
    return next;
};
